import React from 'react';
import { Text } from '@vibe/core';
import type {
  BoardResourcesReportResponse,
  BoardResourcesRow,
  BoardPersonSubRow,
  ReportColumn,
  ReportScope,
} from '../../api/report.api';
import { formatHours } from '../../utils/timesheet-capacity';

// ─── Compat: handle old data shape (weeklyCells / weekColumns) from store ───

/* eslint-disable @typescript-eslint/no-explicit-any */
function getCells(obj: any): number[] {
  return obj.cells ?? obj.weeklyCells ?? [];
}
function getColumns(data: any): ReportColumn[] {
  return data.columns ?? data.weekColumns ?? [];
}
function getScope(data: any): ReportScope {
  return data.scope ?? 'week';
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Max hours value across all cells for scaling the bar width */
function getMaxHours(rows: BoardResourcesRow[]): number {
  let max = 0;
  for (const row of rows) {
    for (const h of getCells(row)) {
      if (h > max) max = h;
    }
    for (const person of row.personSubRows) {
      for (const h of getCells(person)) {
        if (h > max) max = h;
      }
    }
  }
  return max || 1;
}

const SCOPE_LABELS: Record<string, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
};

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  data: BoardResourcesReportResponse;
}

const BoardResourcesTable: React.FC<Props> = ({ data }) => {
  const columns = getColumns(data);
  const scope = getScope(data);
  const { rows, period } = data;
  const maxHours = React.useMemo(() => getMaxHours(rows), [rows]);
  const useBars = scope !== 'day';

  return (
    <div className="rp-report-table__wrapper">
      {/* Period label */}
      <Text
        type={Text.types.TEXT2}
        style={{ color: '#676879', marginBottom: 8 }}
      >
        Period: {period.startDate} – {period.endDate} ({SCOPE_LABELS[scope] ?? scope} view)
      </Text>

      <div className="rp-report-table__scroll">
        <table className={`rp-report-table ${useBars ? 'rp-board-resources-table' : ''}`}>
          <thead>
            <tr>
              <th className="rp-report-table__header-fixed rp-board-resources-table__col-name">
                Board name
              </th>
              <th className="rp-report-table__header-fixed rp-board-resources-table__col-total">
                Total
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={useBars
                    ? 'rp-board-resources-table__week-header'
                    : `rp-report-table__day-header ${col.isWeekend ? 'rp-report-table__day-header--weekend' : ''}`
                  }
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <BoardRow
                key={row.boardId}
                row={row}
                columns={columns}
                maxHours={maxHours}
                useBars={useBars}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Report description */}
      <div className="rp-report-description">
        <div className="rp-report-description__title">How to read this report</div>
        <p className="rp-report-description__text">
          This report shows <strong>remaining estimated hours per board</strong> (from today forward), broken down by time period.
          Each board row reveals individual team member contributions underneath. The{' '}
          <strong>Total</strong> column sums all hours across the entire reporting period.
        </p>
        {useBars ? (
          <>
            <div className="rp-report-description__subtitle">Bar visualization</div>
            <div className="rp-report-description__legend">
              <span className="rp-report-description__swatch rp-report-description__swatch--board-bar" />
              <span>Blue bar &mdash; board-level total hours for the period</span>
            </div>
            <div className="rp-report-description__legend">
              <span className="rp-report-description__swatch rp-report-description__swatch--person-bar" />
              <span>Green bar &mdash; individual person&apos;s hours on that board</span>
            </div>
            <p className="rp-report-description__text">
              Bar width is relative &mdash; the longest bar represents the highest hour value across all boards and people.
              Switch to daily scope to see numeric values instead of bars.
            </p>
          </>
        ) : (
          <>
            <div className="rp-report-description__subtitle">Cell values</div>
            <p className="rp-report-description__text">
              Each cell shows the <strong>number of remaining estimated hours</strong> for that day.
              Empty cells indicate no hours remaining. Weekend columns are grayed out.
              Switch to weekly or monthly scope to see a bar chart visualization.
            </p>
          </>
        )}
        <div className="rp-report-description__subtitle">Key details</div>
        <ul className="rp-report-description__list">
          <li>
            Boards are sorted by <strong>total remaining hours</strong> (highest first). People within each board are also sorted by their total hours.
          </li>
          <li>
            Hours reflect <strong>remaining estimates</strong>: for total-hours items, logged worklogs are subtracted
            from the Original Estimate and the remainder is spread across future working days.
            Hours Per Day items show their daily rate unchanged (worklogs are not subtracted).
          </li>
          <li>
            Only boards included in the report configuration are shown. If no boards were selected, the report includes all configured boards (up to 100).
          </li>
        </ul>
      </div>
    </div>
  );
};

// ─── Board row ──────────────────────────────────────────────────────────────

const BoardRow: React.FC<{
  row: BoardResourcesRow;
  columns: ReportColumn[];
  maxHours: number;
  useBars: boolean;
}> = ({ row, columns, maxHours, useBars }) => {
  const cells = getCells(row);
  return (
    <>
      <tr className="rp-board-resources-table__board-row">
        <td className="rp-report-table__header-fixed rp-board-resources-table__col-name">
          <strong>{row.boardName}</strong>
        </td>
        <td className="rp-report-table__header-fixed rp-board-resources-table__col-total">
          <strong>{formatHours(row.totalScheduledHours)}</strong>
        </td>
        {cells.map((hours, i) => (
          <td
            key={i}
            className={useBars
              ? 'rp-board-resources-table__cell'
              : `rp-report-table__cell ${columns[i]?.isWeekend ? 'rp-report-table__cell--off' : hours === 0 ? 'rp-report-table__cell--zero' : ''}`
            }
          >
            {useBars
              ? <HoursBar hours={hours} maxHours={maxHours} isBoardRow />
              : (columns[i]?.isWeekend ? '' : (hours === 0 ? '' : formatHours(hours)))
            }
          </td>
        ))}
      </tr>
      {row.personSubRows.map((person) => (
        <PersonRow
          key={person.userId}
          person={person}
          columns={columns}
          maxHours={maxHours}
          useBars={useBars}
        />
      ))}
    </>
  );
};

// ─── Person sub-row ─────────────────────────────────────────────────────────

const PersonRow: React.FC<{
  person: BoardPersonSubRow;
  columns: ReportColumn[];
  maxHours: number;
  useBars: boolean;
}> = ({ person, columns, maxHours, useBars }) => {
  const cells = getCells(person);
  return (
    <tr className="rp-board-resources-table__person-row">
      <td className="rp-report-table__header-fixed rp-board-resources-table__col-name rp-board-resources-table__col-name--person">
        {person.userName}
      </td>
      <td className="rp-report-table__header-fixed rp-board-resources-table__col-total rp-board-resources-table__col-total--person">
        {formatHours(person.totalScheduledHours)}
      </td>
      {cells.map((hours, i) => (
        <td
          key={i}
          className={useBars
            ? 'rp-board-resources-table__cell'
            : `rp-report-table__cell ${columns[i]?.isWeekend ? 'rp-report-table__cell--off' : hours === 0 ? 'rp-report-table__cell--zero' : ''}`
          }
        >
          {useBars
            ? <HoursBar hours={hours} maxHours={maxHours} />
            : (columns[i]?.isWeekend ? '' : (hours === 0 ? '' : formatHours(hours)))
          }
        </td>
      ))}
    </tr>
  );
};

// ─── Hours bar (only used for week/month scope) ─────────────────────────────

const HoursBar: React.FC<{
  hours: number;
  maxHours: number;
  isBoardRow?: boolean;
}> = ({ hours, maxHours, isBoardRow }) => {
  if (hours === 0) return null;
  const widthPct = Math.min((hours / maxHours) * 100, 100);

  return (
    <div className="rp-board-resources-table__bar-container">
      <div
        className={`rp-board-resources-table__bar ${
          isBoardRow ? 'rp-board-resources-table__bar--board' : ''
        }`}
        style={{ width: `${widthPct}%` }}
      />
      <span className="rp-board-resources-table__bar-label">
        {formatHours(hours)}
      </span>
    </div>
  );
};

export default BoardResourcesTable;
